import AnimationToggle from 'components/AnimationToggle';
import ButtonC, { BUTTON_HOVERSTYLE } from 'components/ButtonC';
import Footer from 'components/Footer';
import StepperContainer from 'containers/StepperContainer';
import { VIEW } from 'helper/js/constants';
import { isAbTestVerticalScrollEnabled, isKauflandWebView } from 'helper/js/helper';
import { useFlippyNavigate, useFlippyView } from 'helper/js/hooks/router';
import { hasAnimatedLinks } from 'providers/Animation/selectors';
import { useLanguage } from 'providers/Language';
import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

type OwnProps = {
  hasError: boolean;
};

const FooterContainer = ({ hasError }: OwnProps) => {
  const { isSmMin } = useSelector((state: AppState) => state.browser);
  const noMarginals = useSelector((state: AppState) => state.settings.noMarginals);
  const flippyView = useFlippyView();
  const recommendedProductsLength = useSelector(
    (state: AppState) => state.recommendationProducts.count,
  );
  const { t } = useLanguage();
  const flippyNavigate = useFlippyNavigate();
  const hasAnimations = useSelector(hasAnimatedLinks);

  const updateBrowserUrl = useCallback((view) => flippyNavigate({ view }), [flippyNavigate]);

  const isKauflandAppWebView = isKauflandWebView();
  const isAbTestVerticalScrollActive = isAbTestVerticalScrollEnabled(isSmMin);

  const updateView = useCallback(
    (view: string) => updateBrowserUrl(flippyView !== view ? view : VIEW.FLYER),
    [flippyView, updateBrowserUrl],
  );

  const handleShowRecommendedProducts = useCallback(() => {
    updateView(VIEW.RECOMMENDEDPRODUCTS);
  }, [updateView]);

  const handleToggleStepper = useCallback(() => {
    updateView(VIEW.OVERVIEW);
  }, [updateView]);

  const renderFooterLeftContent = useMemo(
    () => !hasError && hasAnimations && <AnimationToggle />,
    [hasAnimations, hasError],
  );

  const renderFooterRightContent = useMemo(
    () => (
      <>
        {!hasError && hasAnimations && (
          <StepperContainer onClick={handleToggleStepper} showArrowButtons={true} />
        )}
        {recommendedProductsLength > 0 && (
          <ButtonC
            inColumn
            hoverStyle={BUTTON_HOVERSTYLE.ICON}
            icon={`star--${import.meta.env.VITE_THEME}`}
            ariaLabel={t('recommendationButtonText')}
            onClick={handleShowRecommendedProducts}
            indicator={recommendedProductsLength}
            active={flippyView === VIEW.RECOMMENDEDPRODUCTS}
            className={`footer__button footer__button--${
              import.meta.env.VITE_THEME
            } footer__button--recommended-products`}
          />
        )}
      </>
    ),
    [
      recommendedProductsLength,
      flippyView,
      hasError,
      hasAnimations,
      t,
      handleShowRecommendedProducts,
      handleToggleStepper,
    ],
  );

  const renderCenterContent = useMemo(() => {
    if (hasError) return null;

    if (isAbTestVerticalScrollActive) {
      return (
        <>
          {hasAnimations && <AnimationToggle />}
          <StepperContainer
            onClick={handleToggleStepper}
            showArrowButtons={false}
          />
        </>
      );
    }

    return (
      !hasAnimations && (
        <StepperContainer
          onClick={handleToggleStepper}
          showArrowButtons={true}
        />
      )
    );
  }, [hasError, hasAnimations, handleToggleStepper, isAbTestVerticalScrollActive]);

  return (
    <Footer
      showFooter={!noMarginals && !isSmMin && !isKauflandAppWebView}
      left={!isAbTestVerticalScrollActive && renderFooterLeftContent}
      right={!isAbTestVerticalScrollActive && renderFooterRightContent}
      center={renderCenterContent}
      isAbTestVerticalScrollActive={isAbTestVerticalScrollActive}
    />
  );
};

export default memo(FooterContainer);
